import { ChangeEvent, FC } from "react";
import { useTranslation } from "react-i18next";
import { UiButton } from "@domun/component-library";
import FieldAutocompleteAddress from "@dashboard/components/field-autocomplete-address";
import FieldDropdown from "@dashboard/components/field-dropdown/FieldDropdown";
import FieldText from "@dashboard/components/field-text/field-text";
import { PlatformCountry, ResponseMapType } from "@dashboard/common/types";
import { DeliveryCustomer } from "../../entities/delivery.entities";
import { DeliveryDestinationLocationWrapper } from "./delivery-destination-location.styled";

type DeliveryDestinationLocationProps = {
  customer: Partial<DeliveryCustomer>;
  country?: PlatformCountry;
  cities: string[];
  addressSelected: boolean;
  locationConfirmed: boolean;
  onCityChange: (city: string) => void;
  onAddressAutocomplete: (map: ResponseMapType) => void;
  onAddressInputChange: (value: string) => void;
  onAddressDetailsChange: (value: string) => void;
  onAdjustOnMap: () => void;
};

export const DeliveryDestinationLocation: FC<DeliveryDestinationLocationProps> = ({
  customer,
  country,
  cities,
  addressSelected,
  locationConfirmed,
  onCityChange,
  onAddressAutocomplete,
  onAddressInputChange,
  onAddressDetailsChange,
  onAdjustOnMap,
}) => {
  const { t } = useTranslation();

  const addressRequiresSelection =
    !!customer.address?.trim() && !addressSelected;
  const addressErrorText = addressRequiresSelection
    ? t("profile.addressesSections.form.address.mustSelectFromList")
    : t("profile.addressesSections.form.address.error");

  const canUseAddress = !!customer.city?.trim();
  const showPendingHint =
    addressSelected && !locationConfirmed && !!customer.address?.trim();

  return (
    <DeliveryDestinationLocationWrapper>
      <FieldDropdown
        id="delivery-destination-city"
        name="city"
        className="delivery-destination-city"
        label={t("delivery.create.customerCity")}
        labelOutside
        value={customer.city || ""}
        isRequired
        addDefault={false}
        disabled={cities.length === 0}
        placeholder={t("delivery.create.location.selectCityFirst")}
        options={cities.map((city) => ({ value: city, label: city }))}
        onChange={(value) => onCityChange(value as string)}
      />

      <FieldAutocompleteAddress
        id="delivery-destination-address"
        name="address"
        label={t("delivery.create.customerAddress")}
        labelOutside
        value={customer.address || ""}
        country={country}
        isRequired
        disabled={!canUseAddress}
        autocomplete="off"
        placeholder={
          canUseAddress
            ? t("profile.addressesSections.form.address.placeholder")
            : t("delivery.create.location.selectCityFirst")
        }
        maxLength={255}
        error={addressRequiresSelection}
        errorText={addressErrorText}
        onChange={onAddressAutocomplete}
        onChangeValue={(e: ChangeEvent<Element>) =>
          onAddressInputChange((e.target as HTMLInputElement).value)
        }
      />

      <FieldText
        id="delivery-destination-address-details"
        name="addressDetails"
        label={t("delivery.create.addressDetails")}
        labelOutside
        value={customer.addressDetails || ""}
        autocomplete="off"
        placeholder={t(
          "profile.addressesSections.form.addressDetails.placeholder"
        )}
        maxLength={255}
        onChange={(e) =>
          onAddressDetailsChange((e.target as HTMLInputElement).value)
        }
      />

      {(locationConfirmed || showPendingHint || !!customer.address?.trim()) && (
        <div
          className={`location-status-card ${
            locationConfirmed ? "confirmed" : "pending"
          }`}
        >
          <p className="location-status-label">
            {locationConfirmed
              ? t("delivery.create.location.locationConfirmed")
              : t("delivery.create.location.confirmOnMapRequired")}
          </p>
          {customer.address && (
            <p className="location-status-text">{customer.address}</p>
          )}
          {customer.city && (
            <p className="location-status-hint">{customer.city}</p>
          )}
          {!locationConfirmed && showPendingHint && (
            <p className="location-status-hint">
              {t("delivery.create.location.locationCardHint")}
            </p>
          )}
        </div>
      )}

      {(addressSelected || (!!customer.address?.trim() && !!customer.city?.trim())) && (
        <div className="location-actions">
          <UiButton onClick={onAdjustOnMap}>
            {locationConfirmed
              ? t("delivery.create.location.adjustOnMap")
              : t("delivery.create.location.mapConfirm")}
          </UiButton>
        </div>
      )}
    </DeliveryDestinationLocationWrapper>
  );
};
